/**
 * 起動後同期の一部として、コメント候補テンプレートの初期投入を扱う。
 * 既存候補が空の場合のみ、履歴画面で使う既定プリセットを保存する。
 */

import { getCommentCandidates } from '@/features/history/api/getCommentCandidates'
import { saveHistoryCommentCandidates } from '@/features/history/api/saveCommentCandidates'
import type { RunToDatabase } from '@/types'
import type {
  CommentCandidatePart,
  InsertCommentCandidateData,
} from '@pkg/database-portfolio'

type LoadCommentCandidates = typeof getCommentCandidates
type SaveCommentCandidates = typeof saveHistoryCommentCandidates

/**
 * コメント候補テンプレートへ付与する既定ラベル一覧。
 */
const defaultCommentCandidateTexts: readonly string[] = [
  'スワップ',
  'ブリッジ',
  '流動性提供',
  'ステーキング',
  '預入(債権トークン付)',
  'DEX/BCG入出金',
  'ICO',
  'レンディング',
  'デビットカード入出金',
]

/**
 * コメント候補テンプレート 1 件分の既定パーツを返す。
 * @param params 候補へ埋め込む固定テキストを受け取る。
 * @returns 取引年月日・通常テキスト・3桁連番で構成されたパーツ一覧を返す。
 */
const createDefaultCommentCandidateParts = ({
  text,
}: {
  readonly text: string
}): CommentCandidatePart[] => {
  return [
    { type: 'action-date' },
    { type: 'text', value: text },
    { type: 'sequence', digits: 3 },
  ]
}

/**
 * 空状態の DB に投入するコメント候補テンプレート一覧を返す。
 * @returns 指定された 9 件の既定候補一覧を返す。
 */
export const createDefaultCommentCandidates =
  (): readonly InsertCommentCandidateData[] => {
    return defaultCommentCandidateTexts.map((text) => ({
      parts: createDefaultCommentCandidateParts({ text }),
    }))
  }

/**
 * コメント候補テンプレートが空の場合のみ既定候補を保存する。
 * @param params DB 読み書き関数と、必要に応じて差し替える入出力関数を受け取る。
 * @returns 値は返さない。
 */
export const ensureDefaultCommentCandidates = async ({
  loadCommentCandidates = getCommentCandidates,
  readFromDatabase,
  saveCommentCandidates = saveHistoryCommentCandidates,
  writeToDatabase,
}: {
  readonly readFromDatabase: RunToDatabase
  readonly writeToDatabase: RunToDatabase | undefined
  readonly loadCommentCandidates?: LoadCommentCandidates
  readonly saveCommentCandidates?: SaveCommentCandidates
}): Promise<void> => {
  if (writeToDatabase === undefined) {
    return
  }

  const existingCandidates = await loadCommentCandidates({ readFromDatabase })
  if (existingCandidates.length > 0) {
    return
  }

  await saveCommentCandidates({
    writeToDatabase,
    candidates: createDefaultCommentCandidates(),
  })
}
